import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch } from 'react-redux';
import { FiSearch, FiX, FiCheck, FiUserPlus, FiUsers } from 'react-icons/fi';
import { getStudentList, assignCourseBulk } from 'actions/studentManagement';
import { Notification } from 'services';

const AVATAR_COLORS = ['#594099', '#1982C4', '#8AC926', '#F5A623', '#FF6B6B', '#4ECDC4', '#D97706', '#059669'];
const STUDENT_PAGE_SIZE = 20;

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || 'ST';
};

const resolveAvatarUrl = (img) => {
    if (!img) return '';
    let raw = '';
    if (typeof img === 'string') raw = img.trim();
    else if (img?.url && typeof img.url === 'string') raw = img.url.trim();
    else if (img?.filePath && typeof img.filePath === 'string') raw = img.filePath.trim();
    else if (img?.image && typeof img.image === 'string') raw = img.image.trim();

    if (!raw) return '';
    const lower = raw.toLowerCase();
    if (lower === 'default.png' || lower.includes('default.png') || lower === 'default.jpg' || lower.includes('default.jpg')) {
        return '';
    }
    // Only show if it is an actual URL (s3 or http/https)
    if (lower.startsWith('http://') || lower.startsWith('https://') || lower.includes('s3')) {
        return raw;
    }
    return '';
};

const StudentAvatar = React.memo(({ name, image }) => {
    const [imgError, setImgError] = useState(false);
    const resolvedImage = resolveAvatarUrl(image);
    const avatarColor = AVATAR_COLORS[Math.abs((name || '').charCodeAt(0)) % AVATAR_COLORS.length];

    useEffect(() => {
        setImgError(false);
    }, [resolvedImage]);

    if (resolvedImage && !imgError) {
        return (
            <img
                src={resolvedImage}
                alt={name}
                onError={() => setImgError(true)}
                style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                }}
            />
        );
    }

    return (
        <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: `${avatarColor}1A`,
            color: avatarColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px',
            fontWeight: 700,
            flexShrink: 0,
        }}>
            {getInitials(name)}
        </div>
    );
});
StudentAvatar.displayName = 'StudentAvatar';

const AddStudentModal = ({
    visible,
    onClose,
    courseId,
    courseName = '',
    courseType = 'course',
    standardId,
    batchName = '',
    sectionName = '',
    onSuccess,
}) => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();

    const [addType, setAddType] = useState('existing'); // 'existing' | 'new'
    const [searchQuery, setSearchQuery] = useState('');
    const [studentOptions, setStudentOptions] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [studentPage, setStudentPage] = useState(1);
    const [hasMoreStudents, setHasMoreStudents] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    // The text box itself — focused on any click inside the search field.
    const searchInputRef = useRef(null);
    const searchDebounceRef = useRef(null);
    // Latest student request — answers to older ones (e.g. a page still loading
    // when the search changed) are ignored.
    const studentRequestIdRef = useRef(0);
    // Search the loaded list belongs to, so scrolling loads more of that list.
    const loadedQueryRef = useRef('');

    // Normalize courseType to one of: 'course' | 'videoResource' | 'studyMaterial' | 'testSeries'
    const normalizeCourseType = useCallback((type) => {
        if (!type) return 'course';
        const lower = String(type).toLowerCase();
        if (lower.includes('video')) return 'videoResource';
        if (lower.includes('study')) return 'studyMaterial';
        if (lower.includes('test')) return 'testSeries';
        return 'course';
    }, []);

    // Loads one page of students; page 1 replaces the list, later pages append.
    const fetchStudents = useCallback((query = '', page = 1) => {
        const requestId = ++studentRequestIdRef.current;
        loadedQueryRef.current = query;
        if (page === 1) setIsSearching(true); else setIsLoadingMore(true);
        const params = {
            search: query.trim() || undefined,
            page,
            limit: STUDENT_PAGE_SIZE,
            skipRedux: true,
        };

        dispatch(getStudentList(params, (response, error) => {
            if (requestId !== studentRequestIdRef.current) return;
            setIsSearching(false);
            setIsLoadingMore(false);
            if (response && !error) {
                const list = Array.isArray(response?.data?.data)
                    ? response.data.data
                    : Array.isArray(response?.data)
                        ? response.data
                        : Array.isArray(response)
                            ? response
                            : [];

                const targetType = normalizeCourseType(courseType);
                // Opened from a section of a classroom course: only students already
                // in this section are hidden — ones in other sections of the same
                // course can still be added here.
                const hasSection = targetType === 'course'
                    && standardId != null && standardId !== '' && String(standardId) !== '-1';
                const isInThisSection = (st) => (Array.isArray(st?.batches) ? st.batches : []).some((b) => {
                    const bId = b?.standardId ?? b?.id;
                    return bId != null && String(bId) === String(standardId);
                });
                // Otherwise students already enrolled in this course aren't offered again.
                const isInThisCourse = (st) => (Array.isArray(st?.courses) ? st.courses : []).some((c) => {
                    const cId = c?.courseId ?? c?.id;
                    if (cId == null || String(cId) !== String(courseId)) return false;
                    // Same id in another course type (e.g. a video course) is a different course.
                    const cType = c?.courseType || c?.type;
                    return !cType || normalizeCourseType(cType) === targetType;
                });

                const isAlreadyAdded = hasSection ? isInThisSection : isInThisCourse;
                const mapped = list.filter((st) => !isAlreadyAdded(st)).map((st, idx) => {
                    const basicprofile = st?.basicprofile || {};
                    const id = st?.id ?? st?._id ?? st?.userId ?? idx;
                    const name = st?.name || basicprofile?.name || st?.username || `Student ${idx + 1}`;
                    const phone = st?.phone || basicprofile?.phone || basicprofile?.mobile || '';
                    const email = st?.email || basicprofile?.email || '';
                    const rollNo = st?.rollNumber || basicprofile?.rollNumber || basicprofile?.rollNo || '';
                    const rawImage = st?.avatar || st?.image || basicprofile?.image || basicprofile?.avatar || '';

                    return { id: Number(id) || id, name, phone, email, rollNo, rawImage };
                });

                setStudentOptions((prev) => {
                    if (page === 1) return mapped;
                    const seen = new Set(prev.map((s) => s.id));
                    return [...prev, ...mapped.filter((s) => !seen.has(s.id))];
                });
                setStudentPage(page);
                // A full page (before hiding enrolled students) means there may be more.
                setHasMoreStudents(list.length === STUDENT_PAGE_SIZE);
            } else {
                if (page === 1) setStudentOptions([]);
                setHasMoreStudents(false);
            }
        }));
    }, [dispatch, courseId, courseType, standardId, normalizeCourseType]);

    const loadMoreStudents = () => {
        if (isSearching || isLoadingMore || !hasMoreStudents) return;
        fetchStudents(loadedQueryRef.current, studentPage + 1);
    };

    // Enrolled students are hidden, so a page can be too short to scroll —
    // keep loading until the dropdown fills or there are no more students.
    useEffect(() => {
        const el = dropdownRef.current;
        if (isDropdownOpen && el && el.scrollHeight <= el.clientHeight) loadMoreStudents();
    }, [isDropdownOpen, studentOptions, hasMoreStudents, isSearching, isLoadingMore]);

    // Reset and load initial students when modal opens
    useEffect(() => {
        if (visible) {
            setAddType('existing');
            setSearchQuery('');
            setSelectedStudents([]);
            setIsDropdownOpen(false);
            fetchStudents('');
        }
    }, [visible, fetchStudents]);

    // Debounce search query input
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchQuery(val);
        setIsDropdownOpen(true);

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
            fetchStudents(val);
        }, 300);
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, []);

    const toggleStudentSelection = (student) => {
        setSelectedStudents((prev) => {
            const exists = prev.some((s) => s.id === student.id);
            if (exists) {
                return prev.filter((s) => s.id !== student.id);
            }
            return [...prev, student];
        });
    };

    const removeStudent = (studentId) => {
        setSelectedStudents((prev) => prev.filter((s) => s.id !== studentId));
    };

    const handleNavigateToAddStudent = () => {
        onClose();
        const basePath = location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';
        const targetPath = location.pathname.endsWith('/student-list')
            ? `${location.pathname}/add-student`
            : `${basePath}/add-student`;

        history.push(targetPath, {
            courseId,
            courseName,
            courseType: normalizeCourseType(courseType),
            standardId,
            batchName,
            sectionName,
        });
    };

    const handleAssignExisting = () => {
        if (selectedStudents.length === 0) return;

        const effectiveCourseType = normalizeCourseType(courseType);
        const payload = {
            courseType: effectiveCourseType,
            courseId: Number(courseId),
            students: selectedStudents.map((s) => Number(s.id)),
        };

        const activeStandardId = standardId ?? location.state?.standardId ?? location.state?.batchId;
        if (effectiveCourseType === 'course' && activeStandardId && activeStandardId !== -1 && activeStandardId !== '-1') {
            payload.standardId = Number(activeStandardId);
        }

        setIsSubmitting(true);
        dispatch(assignCourseBulk(payload, (response, error) => {
            setIsSubmitting(false);
            if (error) {
                Notification.error('Failed to add students', error?.message || 'Something went wrong while assigning students.');
                return;
            }

            const resData = response?.data ?? response;
            const succeeded = resData?.succeeded || [];
            const failed = resData?.failed || [];

            if (succeeded.length > 0 && failed.length === 0) {
                Notification.success('Success', `Course assigned successfully to ${succeeded.length} student${succeeded.length > 1 ? 's' : ''}`);
                onSuccess?.();
                onClose();
            } else if (succeeded.length > 0 && failed.length > 0) {
                Notification.warning(
                    'Partially Assigned',
                    `Assigned to ${succeeded.length} student(s). Failed for ${failed.length}: ${failed.map((f) => f.reason || `User ${f.userId}`).join(', ')}`
                );
                onSuccess?.();
                onClose();
            } else if (failed.length > 0) {
                Notification.error(
                    'Failed to Assign Course',
                    failed.map((f) => f.reason || `User ${f.userId} failed`).join(', ')
                );
            } else {
                Notification.success('Success', 'Student(s) added to course successfully');
                onSuccess?.();
                onClose();
            }
        }));
    };

    const handleClose = () => {
        if (isSubmitting) return;
        setSearchQuery('');
        setSelectedStudents([]);
        setIsDropdownOpen(false);
        onClose();
    };

    const selectedIds = new Set(selectedStudents.map((s) => s.id));

    return (
        <Modal
            visible={visible}
            title={null}
            footer={null}
            closable={false}
            onCancel={handleClose}
            centered
            width={600}
            className="modal-round-corner"
            bodyStyle={{
                padding: '28px 32px',
                borderRadius: '24px',
                fontFamily: 'Roboto, sans-serif',
                backgroundColor: '#FFFFFF',
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Header with single, clean close button */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                        <h2 style={{
                            margin: 0,
                            fontFamily: 'Roboto, sans-serif',
                            fontWeight: 700,
                            fontSize: '22px',
                            color: '#191919',
                            lineHeight: 1.2,
                        }}>
                            Add student
                        </h2>
                        <div style={{
                            marginTop: '6px',
                            fontSize: '14px',
                            color: '#636363',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexWrap: 'wrap',
                        }}>
                            {courseName && (
                                <span style={{ fontWeight: 500, color: '#594099' }}>
                                    {courseName}
                                </span>
                            )}
                            {(batchName || sectionName) && (
                                <>
                                    <span>&middot;</span>
                                    <span>{[batchName, sectionName].filter(Boolean).join(' - ')}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#636363',
                            borderRadius: '50%',
                        }}
                    >
                        <FiX size={22} />
                    </button>
                </div>

                {/* 2 Options Cards */}
                <div style={{ display: 'flex', gap: '14px' }}>
                    {/* Option 1: Add existing student */}
                    <div
                        onClick={() => setAddType('existing')}
                        style={{
                            flex: 1,
                            padding: '16px',
                            borderRadius: '16px',
                            border: addType === 'existing' ? '2px solid #594099' : '1.5px solid #E6E6E6',
                            backgroundColor: addType === 'existing' ? '#F7F5FC' : '#FFFFFF',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '10px',
                                backgroundColor: addType === 'existing' ? '#594099' : '#F1F1F1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: addType === 'existing' ? '#FFFFFF' : '#636363',
                            }}>
                                <FiUsers size={18} />
                            </div>
                            <span style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                border: addType === 'existing' ? '5px solid #594099' : '2px solid #B3B3B3',
                                boxSizing: 'border-box',
                                backgroundColor: '#FFFFFF',
                            }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '15px', color: '#191919' }}>
                                Add existing student
                            </div>
                            <div style={{ fontSize: '13px', color: '#636363', marginTop: '2px', lineHeight: 1.3 }}>
                                Search and enroll registered students
                            </div>
                        </div>
                    </div>

                    {/* Option 2: Add new student */}
                    <div
                        onClick={() => setAddType('new')}
                        style={{
                            flex: 1,
                            padding: '16px',
                            borderRadius: '16px',
                            border: addType === 'new' ? '2px solid #594099' : '1.5px solid #E6E6E6',
                            backgroundColor: addType === 'new' ? '#F7F5FC' : '#FFFFFF',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '10px',
                                backgroundColor: addType === 'new' ? '#594099' : '#F1F1F1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: addType === 'new' ? '#FFFFFF' : '#636363',
                            }}>
                                <FiUserPlus size={18} />
                            </div>
                            <span style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                border: addType === 'new' ? '5px solid #594099' : '2px solid #B3B3B3',
                                boxSizing: 'border-box',
                                backgroundColor: '#FFFFFF',
                            }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '15px', color: '#191919' }}>
                                Add new student
                            </div>
                            <div style={{ fontSize: '13px', color: '#636363', marginTop: '2px', lineHeight: 1.3 }}>
                                Create student &amp; prefill course enrollment
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body Content based on selected option */}
                {addType === 'existing' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {/* Search Input Container with relative positioning */}
                        <div style={{ position: 'relative' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#636363',
                                marginBottom: '8px',
                            }}>
                                Search &amp; Select student(s)
                            </label>

                            <div
                                ref={inputRef}
                                onClick={() => {
                                    setIsDropdownOpen(true);
                                    // Clicks on the padding / search icon also put the cursor in the box.
                                    searchInputRef.current?.focus();
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    height: '46px',
                                    borderRadius: '12px',
                                    border: isDropdownOpen ? '1.5px solid #594099' : '1px solid #D9D9D9',
                                    padding: '0 14px',
                                    backgroundColor: '#FFFFFF',
                                    boxSizing: 'border-box',
                                    cursor: 'text',
                                }}
                            >
                                <FiSearch size={18} color="#8A8A8A" style={{ flexShrink: 0 }} />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onFocus={() => setIsDropdownOpen(true)}
                                    placeholder="Search by name, phone No., email"
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        width: '100%',
                                        fontSize: '15px',
                                        fontFamily: 'Roboto, sans-serif',
                                        color: '#191919',
                                        backgroundColor: 'transparent',
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchQuery('');
                                            fetchStudents('');
                                        }}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            padding: 0,
                                            cursor: 'pointer',
                                            color: '#8A8A8A',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <FiX size={16} />
                                    </button>
                                )}
                            </div>

                            {/* Dropdown Results List placed directly below the input */}
                            {isDropdownOpen && (
                                <div
                                    ref={dropdownRef}
                                    onScroll={(e) => {
                                        const el = e.currentTarget;
                                        if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) loadMoreStudents();
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 6px)',
                                        left: 0,
                                        right: 0,
                                        maxHeight: '230px',
                                        overflowY: 'auto',
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: '14px',
                                        boxShadow: '0 10px 28px rgba(0,0,0,0.14)',
                                        border: '1px solid #E6E6E6',
                                        zIndex: 100,
                                        padding: '6px 0',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    {isSearching ? (
                                        <div style={{
                                            padding: '16px',
                                            textAlign: 'center',
                                            color: '#8A8A8A',
                                            fontSize: '14px',
                                        }}>
                                            Searching students...
                                        </div>
                                    ) : studentOptions.length === 0 && !hasMoreStudents && !isLoadingMore ? (
                                        <div style={{
                                            padding: '16px',
                                            textAlign: 'center',
                                            color: '#8A8A8A',
                                            fontSize: '14px',
                                        }}>
                                            No students found
                                        </div>
                                    ) : (
                                        studentOptions.map((student) => {
                                            const isSelected = selectedIds.has(student.id);

                                            return (
                                                <div
                                                    key={student.id}
                                                    onClick={() => toggleStudentSelection(student)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '10px 16px',
                                                        cursor: 'pointer',
                                                        backgroundColor: isSelected ? '#F6F3FB' : 'transparent',
                                                        transition: 'background 0.15s ease',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) e.currentTarget.style.backgroundColor = '#F9F9F9';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                                                        <StudentAvatar
                                                            name={student.name}
                                                            image={student.rawImage}
                                                        />
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                color: '#191919',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                                {student.name}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '12px',
                                                                color: '#636363',
                                                                marginTop: '2px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                                {[student.phone, student.rollNo ? `Roll: ${student.rollNo}` : null, student.email].filter(Boolean).join(' \u2022 ')}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Selected indicator checkmark (no checkbox) */}
                                                    {isSelected && (
                                                        <FiCheck size={18} color="#594099" style={{ flexShrink: 0, marginLeft: '12px' }} />
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                    {!isSearching && (isLoadingMore || (hasMoreStudents && studentOptions.length === 0)) && (
                                        <div style={{
                                            padding: '10px 16px',
                                            textAlign: 'center',
                                            color: '#8A8A8A',
                                            fontSize: '13px',
                                        }}>
                                            Loading more students...
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Selected Students Chips */}
                        {selectedStudents.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 500, color: '#594099' }}>
                                    {selectedStudents.length} student{selectedStudents.length > 1 ? 's' : ''} selected
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    maxHeight: '90px',
                                    overflowY: 'auto',
                                    padding: '4px 0',
                                }}>
                                    {selectedStudents.map((st) => (
                                        <span
                                            key={st.id}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '4px 10px',
                                                borderRadius: '20px',
                                                backgroundColor: '#EDE9FE',
                                                color: '#594099',
                                                fontSize: '13px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            <span style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {st.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeStudent(st.id)}
                                                style={{
                                                    border: 'none',
                                                    background: 'transparent',
                                                    cursor: 'pointer',
                                                    color: '#594099',
                                                    padding: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <FiX size={14} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{
                        padding: '18px 20px',
                        backgroundColor: '#F8F9FA',
                        borderRadius: '16px',
                        border: '1px solid #EAEAEA',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                    }}>
                        <div style={{ fontWeight: 600, fontSize: '15px', color: '#191919' }}>
                            Add a new student
                        </div>
                        <p style={{ margin: 0, fontSize: '14px', color: '#636363', lineHeight: 1.5 }}>
                            You will be redirected to the student registration page. The course enrollment details for{' '}
                            <strong style={{ color: '#594099' }}>{courseName || 'this course'}</strong> will be automatically pre-selected.
                        </p>
                    </div>
                )}

                {/* Footer Action Buttons */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '8px',
                    paddingTop: '16px',
                    borderTop: '1px solid #EBEBEB',
                }}>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        style={{
                            height: '42px',
                            padding: '0 20px',
                            borderRadius: '10px',
                            border: '1px solid #E0E0E0',
                            backgroundColor: '#FFFFFF',
                            color: '#636363',
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        }}
                    >
                        CANCEL
                    </button>

                    {addType === 'existing' ? (
                        <button
                            type="button"
                            onClick={handleAssignExisting}
                            disabled={selectedStudents.length === 0 || isSubmitting}
                            style={{
                                height: '42px',
                                padding: '0 24px',
                                borderRadius: '10px',
                                border: 'none',
                                backgroundColor: '#594099',
                                color: '#FFFFFF',
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                fontWeight: 700,
                                cursor: (selectedStudents.length === 0 || isSubmitting) ? 'not-allowed' : 'pointer',
                                opacity: (selectedStudents.length === 0 || isSubmitting) ? 0.6 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            {isSubmitting ? 'ADDING...' : `ADD STUDENT${selectedStudents.length > 1 ? `S (${selectedStudents.length})` : ''}`}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleNavigateToAddStudent}
                            style={{
                                height: '42px',
                                padding: '0 24px',
                                borderRadius: '10px',
                                border: 'none',
                                backgroundColor: '#594099',
                                color: '#FFFFFF',
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}
                        >
                            CONTINUE
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default AddStudentModal;
